import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import QRCode from "qrcode";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import Iconify from "../../minimals/components/iconify";

interface IProps {
  open: boolean;
  orderNumber: string;
  onClose: () => void;
}

interface BarcodeRow {
  id: number;
  documentType: RecordableBarcodeOption;
  additionalDescription: string;
  selected: boolean;
}

interface RecordableBarcodeOption {
  id: number;
  description: string;
  action: number;
}

interface RecordableBarcode {
  barcodeValue: string;
  orderNumber: string;
  type: number;
  typeDescription: string;
  additionalDescription: string | null;
  saveLocation: string;
}

const newRow = (id: number, documentType: RecordableBarcodeOption, selected = false): BarcodeRow => ({
  id,
  documentType,
  additionalDescription: "",
  selected
});

const escapeHtml = (value: string) =>
  value.replace(/[&<>'"]/g, (character) => {
    const entities: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "'": "&#39;",
      '"': "&quot;"
    };
    return entities[character];
  });

const qrCodeSvg = async (value: string) => {
  const svg = await QRCode.toString(value, {
    type: "svg",
    errorCorrectionLevel: "H",
    margin: 4
  });

  return svg.replace("<svg", '<svg class="qr-code" role="img" aria-label="Document QR code"');
};

const printBarcodes = async (orderNumber: string, rows: RecordableBarcode[]) => {
  const labels = rows.map((row) => {
    const matchingRows = rows.filter((currentRow) => currentRow.type === row.type);
    const copyIndex = matchingRows.findIndex((currentRow) => currentRow.barcodeValue === row.barcodeValue) + 1;

    return {
      ...row,
      copyIndex,
      quantity: matchingRows.length
    };
  });

  const cards = (
    await Promise.all(
      labels.map(async ({ barcodeValue, typeDescription, additionalDescription, saveLocation, copyIndex, quantity }) => {
        const trimmedDescription = additionalDescription?.trim();
        const qrCode = await qrCodeSvg(barcodeValue);
        return `
        <section class="label">
          <div class="heading">
            <div>
              <div class="eyebrow">TITLEQ DOCUMENT QR CODE</div>
              <h1>${escapeHtml(typeDescription)}</h1>
            </div>
            ${quantity > 1 ? `<div class="copy">Copy ${copyIndex} of ${quantity}</div>` : ""}
          </div>
          ${qrCode}
          <div class="barcode-value">${escapeHtml(barcodeValue)}</div>
          <div class="order">Order <strong>${escapeHtml(orderNumber)}</strong></div>
          ${
            trimmedDescription
              ? `<div class="additional-description">Additional Description: <strong>${escapeHtml(trimmedDescription)}</strong></div>`
              : ""
          }
          <div class="destination">Uploaded document will be saved to ${escapeHtml(saveLocation)}</div>
          <div class="instructions">Place this sheet at the front of the document before scanning.</div>
        </section>`;
      })
    )
  ).join("");

  const printFrame = document.createElement("iframe");
  printFrame.setAttribute("title", "Print document QR codes");
  printFrame.style.position = "fixed";
  printFrame.style.right = "0";
  printFrame.style.bottom = "0";
  printFrame.style.width = "1px";
  printFrame.style.height = "1px";
  printFrame.style.border = "0";
  printFrame.style.opacity = "0";
  document.body.appendChild(printFrame);

  const printDocument = printFrame.contentDocument;
  if (!printDocument || !printFrame.contentWindow) {
    printFrame.remove();
    throw new Error("The print window could not be opened.");
  }

  printDocument.open();
  printDocument.write(`<!doctype html>
    <html>
      <head>
        <title>Document QR Codes - ${escapeHtml(orderNumber)}</title>
        <style>
          @page { size: letter portrait; margin: 0.5in; }
          * { box-sizing: border-box; }
          body { margin: 0; color: #161c24; font-family: Arial, sans-serif; }
          .label { min-height: 9.9in; display: flex; flex-direction: column; justify-content: center; break-after: page; page-break-after: always; }
          .label:last-child { break-after: auto; page-break-after: auto; }
          .heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
          .eyebrow { color: #637381; font-size: 11pt; font-weight: 700; letter-spacing: 0.12em; }
          h1 { margin: 10px 0 8px; font-size: 24pt; line-height: 1.2; }
          .copy { white-space: nowrap; border: 1px solid #c4cdd5; border-radius: 8px; padding: 8px 12px; font-size: 11pt; font-weight: 700; }
          .qr-code { display: block; width: 2.5in; height: 2.5in; margin: 36px auto 0; }
          .barcode-value { margin-top: 10px; text-align: center; font-family: Menlo, Consolas, monospace; font-size: 10pt; }
          .order { margin-top: 18px; text-align: center; font-size: 18pt; }
          .additional-description { margin-top: 14px; text-align: center; font-size: 13pt; }
          .destination { margin-top: 22px; text-align: center; color: #637381; font-size: 11pt; }
          .instructions { margin-top: 64px; padding-top: 20px; border-top: 1px solid #dfe3e8; color: #637381; text-align: center; font-size: 11pt; }
        </style>
      </head>
      <body>${cards}</body>
    </html>`);
  printDocument.close();

  const cleanUp = () => window.setTimeout(() => printFrame.remove(), 500);
  printFrame.contentWindow.addEventListener("afterprint", cleanUp, { once: true });
  window.setTimeout(() => {
    printFrame.contentWindow?.focus();
    printFrame.contentWindow?.print();
  }, 100);
  window.setTimeout(cleanUp, 60_000);
};

export default function PrintDocumentBarcodes({ open, orderNumber, onClose }: IProps) {
  const [options, setOptions] = useState<RecordableBarcodeOption[]>([]);
  const [rows, setRows] = useState<BarcodeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attemptedPrint, setAttemptedPrint] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setRows([]);
    setAttemptedPrint(false);
    setLoadError(null);
    setPrinting(false);
    setLoading(true);

    axios
      .get<RecordableBarcodeOption[]>("/api/recordablestracking/recordables/barcodes/getOptions")
      .then(({ data }) => {
        const barcodeOptions = data ?? [];
        setOptions(barcodeOptions);
        setRows(barcodeOptions.map((barcodeOption, index) => newRow(index + 1, barcodeOption)));
      })
      .catch(() => {
        setOptions([]);
        setRows([]);
        setLoadError("Recordable document types could not be loaded. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [open, orderNumber]);

  const selectedBarcodeRows = useMemo(() => rows.filter((row) => row.selected), [rows]);
  const totalBarcodes = useMemo(() => selectedBarcodeRows.length, [selectedBarcodeRows]);

  const updateAdditionalDescription = (id: number, additionalDescription: string) => {
    setRows((currentRows) => currentRows.map((row) => (row.id === id ? { ...row, additionalDescription } : row)));
  };

  const addDocumentType = (documentType: RecordableBarcodeOption) => {
    setRows((currentRows) => {
      const nextRowId = currentRows.reduce((currentMax, row) => Math.max(currentMax, row.id), 0) + 1;
      return [...currentRows, newRow(nextRowId, documentType, true)];
    });
  };

  const removeRow = (id: number) => {
    setRows((currentRows) => currentRows.filter((row) => row.id !== id));
  };

  const toggleDocumentType = (documentType: RecordableBarcodeOption, checked: boolean) => {
    setRows((currentRows) => {
      const matchingRows = currentRows.filter((row) => row.documentType.id === documentType.id);
      const firstMatchingRow = matchingRows[0];

      if (!firstMatchingRow) {
        const nextRowId = currentRows.reduce((currentMax, row) => Math.max(currentMax, row.id), 0) + 1;
        return [...currentRows, newRow(nextRowId, documentType, checked)];
      }

      if (checked) {
        return currentRows.map((row) => (row.id === firstMatchingRow.id ? { ...row, selected: true } : row));
      }

      return currentRows.filter((row) => row.documentType.id !== documentType.id || row.id === firstMatchingRow.id).map((row) =>
        row.id === firstMatchingRow.id
          ? {
              ...row,
              selected: false
            }
          : row
      );
    });
  };

  const handlePrint = async () => {
    setAttemptedPrint(true);
    if (totalBarcodes === 0) {
      return;
    }

    try {
      setPrinting(true);
      setLoadError(null);
      const { data } = await axios.post<RecordableBarcode[]>("/api/recordablestracking/recordables/barcodes/generate", {
        orderNumber,
        documents: selectedBarcodeRows.map((row) => ({
          type: row.documentType.id,
          action: row.documentType.action,
          description: row.additionalDescription
        }))
      });
      await printBarcodes(orderNumber, data);
      onClose();
    } catch {
      setLoadError("The QR codes could not be prepared for printing. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontSize: 18, pb: 1.25 }}>Print Document QR Codes</DialogTitle>
      <DialogContent sx={{ pb: 1.5 }}>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
          Check each document type to include for order {orderNumber}. Each document can have its own optional additional
          description.
        </Typography>

        <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
          Each QR code sheet will show the exact Post Closing &gt; Recordings save location for that document.
        </Alert>

        {loadError && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
            {loadError}
          </Alert>
        )}

        {attemptedPrint && totalBarcodes === 0 && (
          <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
            Select at least one document type before printing.
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={32} />
          </Stack>
        ) : (
          <Stack spacing={1}>
            {options.map((option) => {
              const documentRows = rows.filter((row) => row.documentType.id === option.id);
              const selectedRows = documentRows.filter((row) => row.selected);
              const isChecked = selectedRows.length > 0;
              const visibleRows = isChecked ? selectedRows : documentRows.slice(0, 1);
              const showDocumentLabels = visibleRows.length > 1;

              return (
                <Box
                  key={option.id}
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1
                  }}
                >
                  <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "flex-start" }} spacing={1.25}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={(event) => toggleDocumentType(option, event.target.checked)}
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={<Typography variant="body2">{option.description}</Typography>}
                      sx={{
                        alignItems: "center",
                        flexShrink: 0,
                        mr: 0,
                        minHeight: 32,
                        mt: { sm: 0.125 },
                        width: { xs: "100%", sm: 230 }
                      }}
                    />

                    <Stack spacing={0.5} sx={{ flex: 1, width: "100%" }}>
                      {visibleRows.map((row, index) => (
                        <Stack
                          key={row.id}
                          direction={{ xs: "column", sm: "row" }}
                          spacing={0.75}
                          alignItems={{ xs: "stretch", sm: "center" }}
                        >
                          {showDocumentLabels && (
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              sx={{ flexShrink: 0, minWidth: { sm: 82 } }}
                            >
                              Document {index + 1}
                            </Typography>
                          )}
                          <TextField
                            fullWidth
                            label="Additional Description"
                            placeholder="Optional"
                            size="small"
                            value={row.additionalDescription}
                            onChange={(event) => updateAdditionalDescription(row.id, event.target.value)}
                            inputProps={{ "aria-label": `${option.description} document ${index + 1} additional description` }}
                            sx={{
                              "& .MuiInputBase-input": {
                                py: 0.75
                              }
                            }}
                          />
                          <IconButton
                            aria-label={`Remove ${option.description} QR code ${index + 1}`}
                            color="error"
                            disabled={!isChecked || selectedRows.length === 1}
                            size="small"
                            onClick={() => removeRow(row.id)}
                            sx={{
                              height: 30,
                              visibility: isChecked && selectedRows.length > 1 ? "visible" : "hidden",
                              width: 30
                            }}
                          >
                            <Iconify icon="eva:trash-2-outline" />
                          </IconButton>
                        </Stack>
                      ))}

                      {isChecked && (
                        <Box sx={{ mt: 0.25 }}>
                          <Button size="small" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => addDocumentType(option)}>
                            Add Document
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button variant="outlined" color="inherit" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<Iconify icon="eva:printer-outline" />}
          disabled={loading || printing || options.length === 0}
          onClick={handlePrint}
        >
          {printing ? "Preparing..." : `Print ${totalBarcodes} ${totalBarcodes === 1 ? "QR Code" : "QR Codes"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
